import MarkdownIt from 'markdown-it';
import mila from 'markdown-it-link-attributes';
import mentionPlugin from './markdownIt/link';

const setImageSizing = inlineToken => {
  const imgSrc = inlineToken.attrGet('src');
  if (!imgSrc) return;
  const url = new URL(imgSrc);
  const width = url.searchParams.get('cw_image_width');
  if (width) {
    inlineToken.attrSet(
      'style',
      `width: ${width}; max-width: 100%; height: auto;`
    );
    return;
  }
  const height = url.searchParams.get('cw_image_height');
  if (height) inlineToken.attrSet('style', `height: ${height};`);
};

const processInlineToken = blockToken => {
  blockToken.children.forEach(inlineToken => {
    if (inlineToken.type === 'image') {
      setImageSizing(inlineToken);
    }
  });
};

const imgResizeManager = md => {
  // If the image URL carries a cw_image_width or cw_image_height query param,
  // add an inline style attribute so the rendered <img> respects the agent's
  // resize choice. Width takes precedence (HC drag-resize); height is kept for
  // legacy messages and the message-signature use case.
  md.core.ruler.after('inline', 'add-image-sizing', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type === 'inline') {
        processInlineToken(blockToken);
      }
    });
  });
};

const createMarkdownInstance = (linkify = true) => {
  return MarkdownIt({
    html: false,
    xhtmlOut: true,
    breaks: true,
    langPrefix: 'language-',
    linkify,
    typographer: true,
    quotes: '\u201c\u201d\u2018\u2019',
    maxNesting: 20,
  })
    .disable(['lheading'])
    .use(mentionPlugin)
    .use(imgResizeManager)
    .use(mila, {
      attrs: {
        class: 'link',
        rel: 'noreferrer noopener nofollow',
        target: '_blank',
      },
    });
};

// Help center article tables persist column widths as an internal
// `<!--cw-colwidths:...-->` comment before the table. It exists only for the
// editor's markdown round-trip and must never surface as text — markdown-it runs
// with `html: false`, which would otherwise escape it into a visible comment in
// rendered/plain output (e.g. dashboard search snippets). Strip it on the way in.
const COLWIDTHS_MARKER_REGEX = /<!--cw-colwidths:[\d,]+-->\r?\n?/g;

const TWITTER_USERNAME_REGEX = /(^|[^@\w])@(\w{1,15})\b/g;
const TWITTER_USERNAME_REPLACEMENT = '$1[@$2](http://twitter.com/$2)';
const TWITTER_HASH_REGEX = /(^|\s)#(\w+)/g;
const TWITTER_HASH_REPLACEMENT = '$1[#$2](https://twitter.com/hashtag/$2)';

class MessageFormatter {
  constructor(
    message,
    isATweet = false,
    isAPrivateNote = false,
    linkify = true
  ) {
    this.message = (message || '').replace(COLWIDTHS_MARKER_REGEX, '');
    this.isAPrivateNote = isAPrivateNote;
    this.isATweet = isATweet;
    this.linkify = linkify;
    this.md = createMarkdownInstance(linkify);
  }

  formatMessage() {
    let updatedMessage = this.message;
    if (this.isATweet && !this.isAPrivateNote) {
      updatedMessage = updatedMessage.replace(
        TWITTER_USERNAME_REGEX,
        TWITTER_USERNAME_REPLACEMENT
      );
      updatedMessage = updatedMessage.replace(
        TWITTER_HASH_REGEX,
        TWITTER_HASH_REPLACEMENT
      );
    }
    let rendered = this.md.render(updatedMessage);

    // Format Captain AI citations into attractive blue badges with inline styles for DOMPurify safety
    const renderBadge = (num, title) => {
      let cleanTitle = title && title !== '#' ? title.trim() : '';

      // Filter out technical internal strings or URLs
      if (
        cleanTitle.toLowerCase().includes('search_documentation') ||
        cleanTitle.toLowerCase().includes('search_reply_documentation') ||
        cleanTitle.startsWith('http://') ||
        cleanTitle.startsWith('https://') ||
        cleanTitle.startsWith('PDF:')
      ) {
        cleanTitle = '';
      }

      if (!cleanTitle) {
        return `<span style="display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; padding: 0 6px; margin: 0 3px; font-size: 11px; font-weight: 700; border-radius: 9999px; background-color: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; vertical-align: middle;">${num}</span>`;
      }
      return `<span style="display: inline-flex; align-items: center; gap: 6px; padding: 2px 8px 2px 4px; margin: 0 4px; font-size: 12px; font-weight: 500; border-radius: 6px; background-color: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; vertical-align: baseline;"><span style="display: inline-flex; align-items: center; justify-content: center; padding: 1px 6px; background-color: #2563eb; color: #ffffff; border-radius: 4px; font-size: 10px; font-weight: 700;">${num}</span>${cleanTitle}</span>`;
    };

    rendered = rendered.replace(
      /\[<a href="([^"]+)"[^>]*>([\s\S]*?)<\/a>\]/g,
      (_, href, inner) => {
        const isNum = /^\d+$/.test(inner.trim());
        const num = isNum ? inner.trim() : '1';
        const title = isNum ? href : inner;
        return renderBadge(num, decodeURIComponent(title));
      }
    );

    rendered = rendered.replace(
      /\[\[(\d+)\]\((.*?)\)\]/g,
      (_, num, title) => renderBadge(num, title)
    );

    rendered = rendered.replace(
      /\[\[(\d+)\]\]/g,
      (_, num) => renderBadge(num, '')
    );

    return rendered;
  }

  get formattedMessage() {
    return this.formatMessage();
  }

  get plainText() {
    const strippedOutHtml = new DOMParser().parseFromString(
      this.formattedMessage,
      'text/html'
    );
    return strippedOutHtml.body.textContent || '';
  }
}

export default MessageFormatter;
